import { useState, useEffect } from "react";
import { mormSigner, AgentDelegationResult, KeypairResult, ChainType } from "../sdk";
import { generateAddressFromMnemonic } from "../utils/addressGeneration";
import type { UseOnboardResult } from "./useOnboard";
import type { UnisatProvider } from "../sdk/signing/bitcoin";

// localStorage keys (shared with useOnboard for integration)
const STORAGE_KEY_MNEMONIC = "onboard_mnemonic";
const STORAGE_KEY_WALLET_ADDRESS = "onboard_wallet_address";
const STORAGE_KEY_CHAIN_TYPE = "onboard_chain_type";
import { useAccount, useSignTypedData } from "wagmi";
import {
  isPhantomSolanaAvailable,
  getPhantomSolanaProvider,
  connectPhantomSolana,
  signSolanaMessage,
  type SolanaMessagePayload,
} from "../sdk/signing/solana";
import {
  isBitcoinWalletAvailable,
  getPreferredBitcoinWallet,
  signBitcoinMessage,
  getBitcoinAddress,
  type BitcoinMessagePayload,
  type OKXBitcoinProvider,
} from "../sdk/signing/bitcoin";

// Supported wallet types
export type SupportedWallet = "metamask" | "phantom" | "onekey" | "okx" | "binance" | "bitget" | "tokenpocket" | "coinbase" | "rainbow";

// Type declaration for Ethereum provider
interface EthereumProvider {
  request(args: { method: "eth_requestAccounts" }): Promise<string[]>;
  request(args: {
    method: "eth_signTypedData_v4";
    params: [string, string];
  }): Promise<string>;
  isMetaMask?: boolean;
  isPhantom?: boolean;
  isOneKey?: boolean;
  isBinance?: boolean;
  isBitEthereum?: boolean; // Bitget Ethereum flag
  isTokenPocket?: boolean; // TokenPocket Ethereum flag
  isCoinbaseWallet?: boolean; // Coinbase Wallet Ethereum flag
  isWalletLink?: boolean; // Legacy WalletLink flag (Coinbase's old bridging tech)
  isRainbow?: boolean; // Rainbow Wallet Ethereum flag
  host?: string; // Coinbase Wallet host URL
  jsonRpcUrl?: string; // Coinbase Wallet JSON-RPC URL
  overridesMetaMask?: boolean; // Coinbase Wallet override flag
  providers?: EthereumProvider[];
}

// Type declaration for Phantom wallet (Ethereum provider)
interface PhantomProvider {
  ethereum?: EthereumProvider;
  isPhantom?: boolean;
}

// Type declaration for Phantom Solana provider
interface PhantomSolanaProvider {
  isPhantom?: boolean;
  isConnected: boolean;
  publicKey: { toString(): string } | null;
  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: "utf8" | "hex"
  ): Promise<{ signature: Uint8Array; publicKey: { toString(): string } }>;
}

// Note: OneKey wallet type is declared inline in the Window interface below
// to match the declaration in bitcoin.ts for TypeScript compatibility

// Type declaration for Bitget Wallet (multi-chain wallet)
interface BitgetProvider {
  isBitKeep?: boolean; // Legacy BitKeep flag (still used by Bitget)
  ethereum?: EthereumProvider & { isBitEthereum?: boolean };
  solana?: {
    isBitKeep?: boolean;
    connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
    disconnect(): Promise<void>;
    signMessage(message: Uint8Array | string, display?: "utf8" | "hex"): Promise<{ signature: Uint8Array; publicKey: { toString(): string } }>;
    publicKey: { toString(): string } | null;
    isConnected: boolean;
  };
  unisat?: {
    // Bitcoin provider (Bitget uses unisat namespace for Bitcoin)
    setIdenties(accounts: string[]): void;
    useIdentity(address: string): void;
    changeAddress(address: string): void;
    requestAccounts(): Promise<string[]>;
    signMessage(message: string): Promise<string>;
  };
  tronLink?: any;
  suiWallet?: any;
  aptos?: any;
}

// sessionStorage keys for agent credentials (temporary browser storage)
const STORAGE_KEY_AGENT_ADDRESS = "agent_delegation_address";
const STORAGE_KEY_AGENT_PRIVATE_KEY = "agent_delegation_private_key";
const STORAGE_KEY_AGENT_EXPIRES_AT = "agent_delegation_expires_at";
const STORAGE_KEY_AGENT_OWNER_ADDRESS = "agent_delegation_owner_address"; // Track which owner this agent belongs to

declare global {
  interface Window {
    ethereum?: EthereumProvider;
    phantom?: PhantomProvider;
    solana?: PhantomSolanaProvider; // Phantom Solana provider
    // $onekey type must match bitcoin.ts declaration exactly
    $onekey?: {
      ethereum?: any; // OneKey Ethereum provider (EthereumProvider type, but using any for compatibility)
      solana?: any; // OneKey Solana provider
      btc?: UnisatProvider; // OneKey Bitcoin provider (must match bitcoin.ts)
    };
    // okxwallet type must match bitcoin.ts declaration exactly, but we add solana for Solana support
    // Note: We use intersection type to add solana while keeping bitcoin.ts compatibility
    okxwallet?: {
      ethereum?: any; // OKX Ethereum provider (EthereumProvider, but using any for compatibility)
      solana?: any; // OKX Solana provider (added here, not in bitcoin.ts)
      bitcoin?: OKXBitcoinProvider; // OKX Bitcoin provider (must match bitcoin.ts)
    };
    BinanceChain?: EthereumProvider; // Binance Chain wallet (legacy)
    bitkeep?: BitgetProvider; // Bitget Wallet (formerly BitKeep)
    tokenpocket?: any; // TokenPocket root namespace indicator (primary detection method)
    tp?: any; // TokenPocket legacy namespace (older versions)
    CoinbaseWalletProvider?: any; // Coinbase Wallet provider class (injected into window)
    WalletLinkProvider?: any; // Legacy WalletLink provider class (Coinbase's old bridging tech)
    coinbaseSolana?: {
      storage?: {
        scope?: string; // Should be 'coinbaseSolana' for genuine Coinbase
      };
    };
  }
}

/**
 * Check if MetaMask wallet provider is truly available (comprehensive detection)
 * Uses multiple verification methods to ensure it's actually MetaMask and not a fake or another wallet
 * 
 * MetaMask injects:
 * - window.ethereum with isMetaMask === true
 * - window.ethereum._metamask (internal object)
 * - window.ethereum.providerInfo.rdns === "io.metamask"
 * - window.ethereum.providerInfo.uuid (v4 UUID)
 * - window.ethereum.providerInfo.name === "MetaMask"
 * - window.solana.isMetaMask === true (if Solana support enabled)
 * 
 * Detection methods (in order of reliability):
 * 1. isMetaMask flag (primary check)
 * 2. _metamask internal object
 * 3. providerInfo.rdns === "io.metamask"
 * 4. providerInfo.name === "MetaMask"
 * 5. UUID presence (v4 format)
 * 6. Solana provider with MetaMask branding
 */
export function isMetaMaskProviderAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  // Method 1: Primary check - isMetaMask flag (most reliable)
  // This is set by initializeProvider from @metamask/providers
  if (ethereum.isMetaMask === true) {
    // Additional verification to ensure it's not a fake
    // Check for _metamask internal object (Method 2)
    if (ethereum._metamask && typeof ethereum._metamask === "object") {
      return true;
    }
    
    // Check for providerInfo.rdns (Method 3)
    if (ethereum.providerInfo?.rdns === "io.metamask") {
      return true;
    }
    
    // Check for providerInfo.name (Method 4)
    if (ethereum.providerInfo?.name === "MetaMask") {
      return true;
    }
    
    // Check for UUID presence (Method 5)
    // MetaMask generates a v4 UUID for each page load
    if (ethereum.providerInfo?.uuid && 
        typeof ethereum.providerInfo.uuid === "string" &&
        ethereum.providerInfo.uuid.length > 0) {
      return true;
    }
    
    // If isMetaMask is true and we don't have conflicting wallets, assume it's MetaMask
    // But exclude known conflicting wallets
    const hasOKX = !!(window as any).okxwallet;
    const hasBinance = isBinanceProviderAvailable();
    const hasOneKey = !!(window as any).$onekey || ethereum.isOneKey;
    const hasPhantom = !!(window as any).phantom?.ethereum || ethereum.isPhantom;
    
    // Only return true if no conflicting wallets are detected
    if (!hasOKX && !hasBinance && !hasOneKey && !hasPhantom) {
      return true;
    }
  }
  
  // Method 6: Check Solana provider with MetaMask branding
  const solana = (window as any).solana;
  if (solana?.isMetaMask === true) {
    return true;
  }
  
  return false;
}

/**
 * Async version that queries wallet_getProviderInfo for additional verification
 * This provides the most reliable detection but requires an async call
 * 
 * @returns Promise that resolves to true if MetaMask is detected, false otherwise
 */
export async function verifyMetaMaskProvider(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  // First do synchronous checks
  if (!isMetaMaskProviderAvailable()) {
    return false;
  }
  
  // Additional async verification via wallet_getProviderInfo
  try {
    const providerInfo = await ethereum.request({ 
      method: 'wallet_getProviderInfo' 
    });
    
    if (providerInfo && typeof providerInfo === "object") {
      // Verify it's MetaMask
      if (providerInfo.name === "MetaMask" || 
          providerInfo.rdns === "io.metamask") {
        return true;
      }
    }
  } catch (error) {
    // If the method is not supported, fall back to synchronous checks
    // This is fine - not all MetaMask versions support wallet_getProviderInfo
    console.debug("wallet_getProviderInfo not available, using synchronous checks");
  }
  
  // Fall back to synchronous check result
  return isMetaMaskProviderAvailable();
}

/**
 * Check if Binance wallet provider is available (immediate check)
 * Uses inpage detection with multiple fallback methods
 * 
 * Modern Binance Web3 Wallet injects:
 * - window.binancew3w = { isExtension: true }
 * - window.ethereum (with Binance-specific properties)
 * - window.solana, window.binancew3w.tron, window.sui, etc.
 * 
 * Legacy Binance Chain Wallet injects:
 * - window.BinanceChain
 */
function isBinanceProviderAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  // Method 1: Modern Binance Web3 Wallet - Primary detection
  // Check for window.binancew3w.isExtension flag (from inpage script)
  if ((window as any).binancew3w?.isExtension === true) {
    return true;
  }
  
  // Method 2: Legacy BinanceChain (still used by older versions)
  if ((window as any).BinanceChain) {
    return true;
  }
  
  // Method 3: window.ethereum with isBinance flag
  const ethereum = (window as any).ethereum;
  if (ethereum?.isBinance) {
    return true;
  }
  
  // Method 4: Check window.ethereum provider name/label for "Binance"
  if (ethereum) {
    // Check if provider name contains "Binance"
    const providerName = ethereum.providerName || ethereum.name || 
                         (ethereum._state?.name) || 
                         (ethereum._metamask?.isUnlocked === undefined ? "Binance" : null);
    if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
      return true;
    }
  }
  
  // Method 5: Fallback - Check for Binance-specific properties
  // Binance wallet injects these properties even when isBinance is false
  if (ethereum?._postMessageToMp && 
      typeof ethereum._postMessageToMp === "function" &&
      ethereum?.namespace === "eip155") {
    return true;
  }
  
  // Method 6: Check for Binance Solana provider
  // Modern Binance injects window.solana or window.binancew3w.solana
  if ((window as any).binancew3w?.isExtension === true && (window as any).binancew3w?.solana) {
    return true;
  }
  
  const solana = (window as any).solana;
  if (solana) {
    // Check for Binance-specific flags
    if (solana.isBinance === true) {
      return true;
    }
    // Check provider name for "Binance"
    const providerName = solana.name || solana.providerName;
    if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
      return true;
    }
    // If binancew3w.isExtension exists, window.solana is likely Binance
    if ((window as any).binancew3w?.isExtension === true) {
      return true;
    }
  }
  
  // Method 7: Check window.ethereum.solana (newer format)
  if ((window as any).ethereum?.solana) {
    return true;
  }
  
  // Method 8: Check window.BinanceChain.solana (legacy)
  if ((window as any).BinanceChain?.solana) {
    return true;
  }
  
  return false;
}

/**
 * Check if Bitget Wallet provider is available (comprehensive detection)
 * Uses multiple verification methods to ensure it's actually Bitget Wallet
 * 
 * Bitget Wallet (formerly BitKeep) injects:
 * - window.bitkeep.isBitKeep === true (primary flag)
 * - window.bitkeep.ethereum.isBitEthereum === true (Ethereum-specific flag, primary for Ethereum detection)
 * - window.bitkeep namespace with multiple sub-providers (unique to Bitget)
 * - window.bitkeep.solana (Solana provider)
 * - window.bitkeep.unisat (Bitcoin provider, primary)
 * - window.unisat (Bitcoin provider shim, optional - only if Bitget is detected via other means)
 * - window.bitkeep.tronLink (Tron provider)
 * - window.bitkeep.suiWallet, window.bitkeep.aptos, etc. (other chains)
 * 
 * Detection methods (in order of reliability):
 * 1. window.bitkeep.isBitKeep === true (primary check for general Bitget detection)
 * 2. window.bitkeep.ethereum.isBitEthereum === true (Ethereum-specific, primary for Ethereum)
 * 3. window.bitkeep namespace exists with multiple providers (multi-chain indicator)
 * 4. window.bitkeep.solana with isBitKeep flag or connect function
 * 
 * Based on analysis of bitget-inpage.txt:
 * - Root object: window.bitkeep = {} (main namespace)
 * - Ethereum: window.bitkeep.ethereum with isBitEthereum: true flag
 * - Solana: window.bitkeep.solana (Solana-compatible provider)
 * - Bitcoin: window.bitkeep.unisat (primary), may also shim window.unisat
 */
export function isBitgetProviderAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const bitkeep = (window as any).bitkeep;
  if (!bitkeep) return false;
  
  // Method 1: Primary check - isBitKeep flag (most reliable general detection)
  // This is the official detection flag per Bitget docs
  if (bitkeep.isBitKeep === true) {
    return true;
  }
  
  // Method 2: Ethereum-specific flag (primary for Ethereum detection)
  // Per analysis: window.bitkeep.ethereum.isBitEthereum === true is the key Ethereum indicator
  if (bitkeep.ethereum?.isBitEthereum === true) {
    return true;
  }
  
  // Method 3: Check for multiple sub-providers (unique to Bitget)
  // Bitget is the only wallet that uses window.bitkeep with multiple providers
  // This is a strong indicator of Bitget (multi-chain wallet)
  const hasEthereum = !!bitkeep.ethereum && typeof bitkeep.ethereum.request === "function";
  const hasSolana = !!bitkeep.solana && typeof bitkeep.solana.connect === "function";
  const hasUnisat = !!bitkeep.unisat; // Bitcoin provider
  const hasTronLink = !!bitkeep.tronLink;
  
  // If we have multiple providers under bitkeep namespace, it's very likely Bitget
  const providerCount = [hasEthereum, hasSolana, hasUnisat, hasTronLink].filter(Boolean).length;
  if (providerCount >= 2) {
    return true;
  }
  
  // Method 4: Check Solana provider with isBitKeep flag
  if (bitkeep.solana?.isBitKeep === true) {
    return true;
  }
  
  // Method 5: Fallback - if bitkeep namespace exists with ethereum provider
  // and no conflicting wallets are detected, assume it's Bitget
  if (hasEthereum) {
    // Exclude known conflicting wallets
    const hasOKX = !!(window as any).okxwallet;
    const hasBinance = isBinanceProviderAvailable();
    const hasOneKey = !!(window as any).$onekey;
    const hasPhantom = !!(window as any).phantom?.ethereum;
    const hasMetaMask = isMetaMaskProviderAvailable();
    
    // Only return true if no conflicting wallets are detected
    if (!hasOKX && !hasBinance && !hasOneKey && !hasPhantom && !hasMetaMask) {
      return true;
    }
  }
  
  return false;
}

/**
 * Get Bitget Wallet Ethereum provider
 * Returns the provider object if available, null otherwise
 * 
 * Per analysis: window.bitkeep.ethereum with isBitEthereum: true flag
 * Supports EIP-1193: request({ method: 'eth_requestAccounts' })
 * Connection state: window.bitkeep.ethereum.selectedAddress and isConnected()
 */
export function getBitgetProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  const bitkeep = (window as any).bitkeep;
  if (!bitkeep) return null;
  
  // Check if it's actually Bitget
  if (!isBitgetProviderAvailable()) {
    return null;
  }
  
  // Return Ethereum provider
  // Per analysis: window.bitkeep.ethereum with isBitEthereum flag
  if (bitkeep.ethereum && typeof bitkeep.ethereum.request === "function") {
    // Verify it has the Bitget Ethereum flag
    if (bitkeep.ethereum.isBitEthereum === true || bitkeep.isBitKeep === true) {
      return bitkeep.ethereum as EthereumProvider;
    }
  }
  
  return null;
}

/**
 * Wait for Bitget wallet to become available (inpage detection with polling)
 * Handles timing issues where Bitget extension injects window.bitkeep asynchronously
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @param interval - Polling interval in milliseconds (default: 100ms)
 * @returns Promise that resolves to true if Bitget is detected, false if timeout
 */
export async function waitForBitgetWallet(
  timeout: number = 3000,
  interval: number = 100
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  // Immediate check
  if (isBitgetProviderAvailable()) {
    return true;
  }
  
  // Wait for document to be ready (if not already)
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const checkReady = () => {
          if (document.readyState === "complete") {
            document.removeEventListener("readystatechange", checkReady);
            resolve();
          }
        };
        document.addEventListener("readystatechange", checkReady);
      }
    });
    
    // Check again after document is ready
    if (isBitgetProviderAvailable()) {
      return true;
    }
  }
  
  // Polling-based detection (handles async injection timing)
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.ceil(timeout / interval);
    
    const poll = () => {
      attempts++;
      
      // Check for Bitget wallet
      if (isBitgetProviderAvailable()) {
        resolve(true);
        return;
      }
      
      // Timeout check
      if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      
      // Continue polling
      setTimeout(poll, interval);
    };
    
    poll();
  });
}

/**
 * Check if TokenPocket Wallet provider is available (comprehensive detection)
 * Uses multiple verification methods to ensure it's actually TokenPocket Wallet
 * 
 * TokenPocket Wallet is a multi-chain wallet that injects providers for:
 * - Ethereum: window.ethereum.isTokenPocket === true (primary flag, official detection method)
 * - Solana: window.solana (when TokenPocket is detected via window.tokenpocket or ethereum.isTokenPocket)
 * - Other chains: Tron, EOS, etc. (via various namespaces)
 * 
 * Detection methods (in order of reliability):
 * 1. window.tokenpocket exists (root namespace indicator - most reliable for multi-chain detection)
 * 2. window.ethereum.isTokenPocket === true (primary check, official flag)
 * 3. window.tp exists (legacy namespace check - TokenPocket was formerly TP Wallet)
 * 4. window.ethereum with TokenPocket-specific properties
 * 
 * Note: This function checks for Ethereum provider availability.
 * For Solana, use isTokenPocketSolanaAvailable().
 */
export function isTokenPocketProviderAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  // Method 1: Primary check - window.tokenpocket root namespace (most reliable indicator)
  // TokenPocket injects window.tokenpocket as a root namespace indicator
  if (typeof (window as any).tokenpocket !== "undefined") {
    return true;
  }
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  // Method 2: Check - isTokenPocket flag (official detection flag per TokenPocket docs)
  if (ethereum.isTokenPocket === true) {
    return true;
  }
  
  // Method 3: Legacy namespace check (window.tp)
  // TokenPocket was formerly known as TP Wallet
  if ((window as any).tp && typeof (window as any).tp === "object") {
    return true;
  }
  
  // Method 4: Check for TokenPocket-specific properties
  // TokenPocket may set additional properties on ethereum provider
  if (ethereum.isTokenPocket !== undefined) {
    return true;
  }
  
  // Method 4: Multi-chain heuristic
  // TokenPocket is a multi-chain wallet, so if we detect multiple chain providers
  // and no other known wallet is detected, it might be TokenPocket
  const hasSolana = !!(window as any).solana;
  const hasUnisat = !!(window as any).unisat;
  const hasMultipleChains = hasSolana && hasUnisat;
  
  // Exclude known conflicting wallets
  const hasOKX = !!(window as any).okxwallet;
  const hasBinance = isBinanceProviderAvailable();
  const hasOneKey = !!(window as any).$onekey;
  const hasPhantom = !!(window as any).phantom?.ethereum;
  const hasMetaMask = isMetaMaskProviderAvailable();
  const hasBitget = isBitgetProviderAvailable();
  
  // If multiple chains detected and no other wallet is present, might be TokenPocket
  // But we need to be careful - only return true if we have strong evidence
  if (hasMultipleChains && !hasOKX && !hasBinance && !hasOneKey && !hasPhantom && !hasMetaMask && !hasBitget) {
    // Additional check: verify it's not just a generic multi-chain setup
    // TokenPocket typically injects window.ethereum with isTokenPocket
    if (ethereum && typeof ethereum.request === "function") {
      // Weak signal, but if no other wallet detected, might be TokenPocket
      return false; // Too weak, don't assume
    }
  }
  
  return false;
}

/**
 * Check if TokenPocket supports Ethereum chain
 * @returns true if TokenPocket Ethereum provider is available
 */
export function isTokenPocketEthereumAvailable(): boolean {
  return isTokenPocketProviderAvailable();
}

/**
 * Check if TokenPocket supports Solana chain
 * @returns true if TokenPocket Solana provider is available
 * 
 * Based on TokenPocket SDK analysis:
 * - TokenPocket injects window.tokenpocket as root namespace
 * - Uses window.solana for Solana provider (similar to Phantom)
 * - Can check tp.isConnected() if window.tp exists (SDK bridge check)
 */
export function isTokenPocketSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const solana = (window as any).solana;
  if (!solana) return false;
  
  // Method 1: PRIMARY - Check window.solana.name property (simplest and most direct)
  // TokenPocket sets name: 'TokenPocket' on the solana provider (confirmed via console inspection)
  // IMPORTANT: Check this FIRST before excluding Phantom, because TokenPocket ALSO sets isPhantom: true for compatibility!
  if (solana.name === 'TokenPocket') {
    return true; // TokenPocket detected via name property (even if isPhantom: true)
  }
  
  // Method 1b: Check features.name (Wallet Standard property)
  // TokenPocket also sets features.name: 'TokenPocket' in the Wallet Standard features object
  if (solana.features && (solana.features as any).name === 'TokenPocket') {
    return true; // TokenPocket detected via features.name (even if isPhantom: true)
  }
  
  // Method 1c: Fallback - Check for TokenPocket private field #e: 'TokenPocket' via object inspection
  // TokenPocket sets a private field #e with value 'TokenPocket' on the solana provider
  try {
    const providerString = String(solana);
    const providerValues = Object.values(solana);
    
    if (providerString.includes('TokenPocket') || 
        providerValues.some((v: any) => String(v) === 'TokenPocket')) {
      return true; // TokenPocket detected via #e field (even if isPhantom: true)
    }
    
    // Check for any symbol that might expose the private field
    const symbols = Object.getOwnPropertySymbols(solana);
    for (const sym of symbols) {
      const value = (solana as any)[sym];
      if (value === 'TokenPocket' || String(value) === 'TokenPocket') {
        return true; // TokenPocket detected (even if isPhantom: true)
      }
    }
  } catch (error) {
    // Ignore errors from inspection attempts
  }
  
  // Exclude Phantom - BUT only if we haven't detected TokenPocket via #e field above
  // TokenPocket sets isPhantom: true for compatibility, so we must check #e first
  if (solana.isPhantom === true) return false;
  
  const tokenpocket = (window as any).tokenpocket;
  const ethereum = (window as any).ethereum;
  const tp = (window as any).tp;
  
  // Method 2: Check - window.tokenpocket root namespace (most reliable)
  // TokenPocket injects window.tokenpocket as a root namespace indicator
  if (typeof tokenpocket !== "undefined") {
    return true;
  }
  
  // Method 3: Check tp.isConnected() if window.tp exists (SDK bridge check)
  // From SDK: tp.isConnected() checks for TPJSBrigeClient or webkit messageHandlers
  if (tp && typeof tp.isConnected === "function") {
    try {
      if (tp.isConnected()) {
        return true;
      }
    } catch (error) {
      // isConnected() may throw in some contexts, ignore
    }
  }
  
  // Method 4: Fallback - Check for TokenPocket Ethereum flag
  if (ethereum?.isTokenPocket === true) {
    return true;
  }
  
  // Method 5: Legacy check - window.tp namespace (older TokenPocket versions)
  if (tp && typeof tp === "object") {
    return true;
  }
  
  return false;
}

/**
 * Get TokenPocket Wallet Ethereum provider
 * Returns the provider object if available, null otherwise
 */
export function getTokenPocketProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return null;
  
  // Check if it's actually TokenPocket
  if (!isTokenPocketProviderAvailable()) {
    return null;
  }
  
  // Return Ethereum provider
  if (ethereum && typeof ethereum.request === "function") {
    return ethereum as EthereumProvider;
  }
  
  return null;
}

/**
 * Wait for TokenPocket wallet to become available (inpage detection with polling)
 * Handles timing issues where TokenPocket extension injects window.ethereum asynchronously
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @param interval - Polling interval in milliseconds (default: 100ms)
 * @returns Promise that resolves to true if TokenPocket is detected, false if timeout
 */
export async function waitForTokenPocketWallet(
  timeout: number = 3000,
  interval: number = 100
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  // Immediate check
  if (isTokenPocketProviderAvailable()) {
    return true;
  }
  
  // Wait for document to be ready (if not already)
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const checkReady = () => {
          if (document.readyState === "complete") {
            document.removeEventListener("readystatechange", checkReady);
            resolve();
          }
        };
        document.addEventListener("readystatechange", checkReady);
      }
    });
    
    // Check again after document is ready
    if (isTokenPocketProviderAvailable()) {
      return true;
    }
  }
  
  // Polling-based detection (handles async injection timing)
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.ceil(timeout / interval);
    
    const poll = () => {
      attempts++;
      
      // Check for TokenPocket wallet
      if (isTokenPocketProviderAvailable()) {
        resolve(true);
        return;
      }
      
      // Timeout check
      if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      
      // Continue polling
      setTimeout(poll, interval);
    };
    
    poll();
  });
}

/**
 * Check if Coinbase Wallet provider is available (robust multi-check detection)
 * Uses multiple verification methods to ensure it's actually Coinbase Wallet and not a fake
 * 
 * Coinbase Wallet injects:
 * - window.ethereum with isCoinbaseWallet === true
 * - window.ethereum.host (must include 'coinbase.com' domain)
 * - window.ethereum.jsonRpcUrl (must include 'coinbase.com' domain)
 * - window.ethereum.isWalletLink === true (legacy flag, true in genuine installs)
 * - window.ethereum.overridesMetaMask === false (genuine doesn't override MetaMask)
 * - window.CoinbaseWalletProvider (injected class)
 * - window.WalletLinkProvider (legacy class)
 * - window.solana with isCoinbaseWallet === true (for Solana support)
 * - window.coinbaseSolana.storage.scope === 'coinbaseSolana' (for Solana)
 * 
 * Detection methods (in order of reliability):
 * 1. isCoinbaseWallet flag (primary check)
 * 2. host property includes 'coinbase.com'
 * 3. jsonRpcUrl property includes 'coinbase.com'
 * 4. isWalletLink flag (legacy, true in genuine)
 * 5. overridesMetaMask === false
 * 6. CoinbaseWalletProvider class exists
 * 7. WalletLinkProvider class exists
 * 8. Behavioral check: Test RPC call (eth_chainId)
 */
export async function isCoinbaseWalletProviderAvailable(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  let provider = ethereum;
  
  // Handle multiple providers (e.g., MetaMask + Coinbase)
  if (Array.isArray(ethereum.providers)) {
    provider = ethereum.providers.find((p: any) => p.isCoinbaseWallet);
    if (!provider) return false;
  } else if (!ethereum.isCoinbaseWallet) {
    return false;
  }
  
  // Check 1: Basic flag (must be true)
  if (!provider.isCoinbaseWallet) {
    return false;
  }
  
  // Check 2: host property must include 'coinbase.com'
  if (typeof provider.host !== "string" || !provider.host.includes("coinbase.com")) {
    return false;
  }
  
  // Check 3: jsonRpcUrl property must include 'coinbase.com'
  if (typeof provider.jsonRpcUrl !== "string" || !provider.jsonRpcUrl.includes("coinbase.com")) {
    return false;
  }
  
  // Check 4: overridesMetaMask should be false (genuine doesn't override MetaMask)
  if (provider.overridesMetaMask !== false && provider.overridesMetaMask !== undefined) {
    return false;
  }
  
  // Check 5: isWalletLink flag (legacy, true in genuine installs)
  if (!provider.isWalletLink) {
    return false;
  }
  
  // Check 6: CoinbaseWalletProvider class exists
  if (typeof (window as any).CoinbaseWalletProvider !== "function") {
    return false;
  }
  
  // Check 7: WalletLinkProvider class exists (legacy)
  if (typeof (window as any).WalletLinkProvider !== "function") {
    return false;
  }
  
  // Check 8: Behavioral check - Test a safe RPC call (eth_chainId)
  try {
    const chainId = await provider.request({ method: "eth_chainId" });
    if (typeof chainId !== "string" || !chainId.startsWith("0x")) {
      return false; // Invalid response format
    }
  } catch (error) {
    console.error("Coinbase Wallet RPC test failed:", error);
    return false; // Fakes may error or respond oddly
  }
  
  return true;
}

/**
 * Synchronous version of Coinbase Wallet detection (without behavioral check)
 * Use this for quick checks, but prefer the async version for final verification
 */
export function isCoinbaseWalletProviderAvailableSync(): boolean {
  if (typeof window === "undefined") return false;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  let provider = ethereum;
  
  // Handle multiple providers (e.g., MetaMask + Coinbase)
  if (Array.isArray(ethereum.providers)) {
    provider = ethereum.providers.find((p: any) => p.isCoinbaseWallet);
    if (!provider) return false;
  } else if (!ethereum.isCoinbaseWallet) {
    return false;
  }
  
  // Check 1: Basic flag (must be true)
  if (!provider.isCoinbaseWallet) {
    return false;
  }
  
  // Check 2: host property must include 'coinbase.com'
  if (typeof provider.host !== "string" || !provider.host.includes("coinbase.com")) {
    return false;
  }
  
  // Check 3: jsonRpcUrl property must include 'coinbase.com'
  if (typeof provider.jsonRpcUrl !== "string" || !provider.jsonRpcUrl.includes("coinbase.com")) {
    return false;
  }
  
  // Check 4: overridesMetaMask should be false (genuine doesn't override MetaMask)
  if (provider.overridesMetaMask !== false && provider.overridesMetaMask !== undefined) {
    return false;
  }
  
  // Check 5: isWalletLink flag (legacy, true in genuine installs)
  if (!provider.isWalletLink) {
    return false;
  }
  
  // Check 6: CoinbaseWalletProvider class exists
  if (typeof (window as any).CoinbaseWalletProvider !== "function") {
    return false;
  }
  
  // Check 7: WalletLinkProvider class exists (legacy)
  if (typeof (window as any).WalletLinkProvider !== "function") {
    return false;
  }
  
  return true;
}

/**
 * Get Coinbase Wallet Ethereum provider
 * Returns the provider object if available, null otherwise
 */
export function getCoinbaseWalletProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return null;
  
  // Handle multiple providers (e.g., MetaMask + Coinbase)
  if (Array.isArray(ethereum.providers)) {
    const coinbaseProvider = ethereum.providers.find((p: any) => p.isCoinbaseWallet);
    if (coinbaseProvider && typeof coinbaseProvider.request === "function") {
      return coinbaseProvider as EthereumProvider;
    }
  } else if (ethereum.isCoinbaseWallet && typeof ethereum.request === "function") {
    return ethereum as EthereumProvider;
  }
  
  return null;
}

/**
 * Check if Coinbase Wallet Solana provider is available
 * Based on coinbase-reqsol.txt analysis:
 * - window.coinbaseSolana exists directly (created at line 13616)
 * - window.coinbaseSolana._storage.scope === 'coinbaseSolana' (storage uses underscore prefix, line 13623)
 * - Has methods: connect(), disconnect(), signMessage(), signTransaction(), sendTransaction()
 * 
 * NOTE: Coinbase Wallet does NOT set window.solana or window.solana.isCoinbaseWallet
 * It only creates window.coinbaseSolana directly
 */
export function isCoinbaseWalletSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const coinbaseSolana = (window as any).coinbaseSolana;
  
  // Primary detection: Check for window.coinbaseSolana directly
  // Coinbase Wallet creates this object directly, not via window.solana
  if (coinbaseSolana && typeof coinbaseSolana === "object") {
    // Verify it's actually Coinbase Wallet by checking storage scope
    // Storage uses underscore prefix: _storage
    // From coinbase-reqsol.txt line 13623: this._storage = new e.ScopedLocalStorage("coinbaseSolana")
    const storage = coinbaseSolana._storage;
    if (storage && storage.scope === "coinbaseSolana") {
      return true;
    }
    
    // If storage check fails but coinbaseSolana exists with expected methods, still consider it valid
    // From coinbase-reqsol.txt: the object has connect, disconnect, signMessage, signTransaction methods
    if (
      typeof coinbaseSolana.connect === "function" &&
      typeof coinbaseSolana.disconnect === "function" &&
      typeof coinbaseSolana.signMessage === "function" &&
      typeof coinbaseSolana.signTransaction === "function"
    ) {
      return true;
    }
  }
  
  return false;
}

/**
 * Wait for Coinbase Wallet to become available (inpage detection with polling)
 * Handles timing issues where Coinbase extension injects window.ethereum asynchronously
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @param interval - Polling interval in milliseconds (default: 100ms)
 * @returns Promise that resolves to true if Coinbase Wallet is detected, false if timeout
 */
export async function waitForCoinbaseWallet(
  timeout: number = 3000,
  interval: number = 100
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  // Immediate check
  if (isCoinbaseWalletProviderAvailableSync()) {
    return true;
  }
  
  // Wait for document to be ready (if not already)
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const checkReady = () => {
          if (document.readyState === "complete") {
            document.removeEventListener("readystatechange", checkReady);
            resolve();
          }
        };
        document.addEventListener("readystatechange", checkReady);
      }
    });
    
    // Check again after document is ready
    if (isCoinbaseWalletProviderAvailableSync()) {
      return true;
    }
  }
  
  // Polling-based detection (handles async injection timing)
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.ceil(timeout / interval);
    
    const poll = () => {
      attempts++;
      
      // Check for Coinbase Wallet
      if (isCoinbaseWalletProviderAvailableSync()) {
        resolve(true);
        return;
      }
      
      // Timeout check
      if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      
      // Continue polling
      setTimeout(poll, interval);
    };
    
    poll();
  });
}

/**
 * Wait for Binance wallet to become available (inpage detection with polling)
 * Handles timing issues where Binance extension injects window.BinanceChain asynchronously
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @param interval - Polling interval in milliseconds (default: 100ms)
 * @returns Promise that resolves to true if Binance is detected, false if timeout
 */
export async function waitForBinanceWallet(
  timeout: number = 3000,
  interval: number = 100
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  // Immediate check
  if (isBinanceProviderAvailable()) {
    return true;
  }
  
  // Wait for document to be ready (if not already)
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const checkReady = () => {
          if (document.readyState === "complete") {
            document.removeEventListener("readystatechange", checkReady);
            resolve();
          }
        };
        document.addEventListener("readystatechange", checkReady);
      }
    });
    
    // Check again after document is ready
    if (isBinanceProviderAvailable()) {
      return true;
    }
  }
  
  // Polling-based detection (handles async injection timing)
  // Binance injects at document_start but may not be immediately available
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.ceil(timeout / interval);
    
    const poll = () => {
      attempts++;
      
      // Check for Binance wallet
      if (isBinanceProviderAvailable()) {
        resolve(true);
        return;
      }
      
      // Timeout check
      if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      
      // Continue polling
      setTimeout(poll, interval);
    };
    
    poll();
  });
}

/**
 * Get Binance wallet provider (with inpage detection)
 * Returns the provider object if available, null otherwise
 * 
 * Priority order:
 * 1. window.binancew3w.isExtension (modern) -> use window.ethereum
 * 2. window.BinanceChain (legacy)
 * 3. window.ethereum with Binance detection
 */
export function getBinanceProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  // Method 1: Modern Binance Web3 Wallet
  // If binancew3w.isExtension exists, use window.ethereum (it's injected by Binance)
  if ((window as any).binancew3w?.isExtension === true) {
    const ethereum = (window as any).ethereum;
    if (ethereum) {
      return ethereum as EthereumProvider;
    }
  }
  
  // Method 2: Legacy BinanceChain (older versions)
  if ((window as any).BinanceChain) {
    return (window as any).BinanceChain as EthereumProvider;
  }
  
  // Method 3: window.ethereum with Binance detection
  const ethereum = (window as any).ethereum;
  if (ethereum) {
    // Check isBinance flag
    if (ethereum.isBinance) {
      return ethereum as EthereumProvider;
    }
    
    // Check provider name/label
    const providerName = ethereum.providerName || ethereum.name || 
                         (ethereum._state?.name);
    if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
      return ethereum as EthereumProvider;
    }
    
    // Check Binance-specific properties
    if (ethereum._postMessageToMp && 
        typeof ethereum._postMessageToMp === "function" &&
        ethereum?.namespace === "eip155") {
      return ethereum as EthereumProvider;
    }
  }
  
  return null;
}

/**
 * Detects which supported wallet is available
 * Returns 'metamask', 'phantom', 'onekey', 'okx', 'binance', 'bitget', 'tokenpocket', 'coinbase', or null
 * 
 * IMPORTANT: Check order matters! 
 * - OneKey must be checked first (provides backward compatibility interfaces)
 * - OKX must be checked before MetaMask (may inject window.ethereum)
 * - Bitget must be checked before Binance and MetaMask (multi-chain wallet)
 * - TokenPocket must be checked before Binance and MetaMask (multi-chain wallet)
 * - Coinbase must be checked before MetaMask (may inject window.ethereum)
 * - Binance must be checked before MetaMask
 * 
 * Smart Detection Heuristic:
 * - If OKX is detected for both Solana AND Bitcoin, then Ethereum provider is very likely OKX
 *   (OKX is a multi-chain wallet, so if Solana and Bitcoin are both OKX, Ethereum must be too)
 * - Bitget uses unique window.bitkeep namespace, so it's easy to detect
 * - TokenPocket uses window.ethereum.isTokenPocket flag (official detection method)
 * - Coinbase uses robust multi-check detection (host, jsonRpcUrl, isWalletLink, etc.)
 */
export function detectSupportedWallet(): SupportedWallet | null {
  if (typeof window === "undefined") return null;

  // Check for Phantom first (most specific, but OneKey masquerades as Phantom)
  if (window.phantom?.ethereum || window.ethereum?.isPhantom) {
    // But exclude OneKey (OneKey provides window.phantom for backward compatibility)
    if (!(window.$onekey || window.ethereum?.isOneKey)) {
      return "phantom";
    }
  }

  // Check for OneKey (highest priority - provides backward compatibility)
  // OneKey provides window.phantom, window.unisat, etc. for backward compatibility
  if (window.$onekey?.ethereum || window.ethereum?.isOneKey || (window as any).$onekey) {
    return "onekey";
  }

  // Smart OKX Detection Heuristic:
  // If OKX Solana AND Bitcoin are both detected, then Ethereum is very likely OKX
  // (OKX is a multi-chain wallet - if both Solana and Bitcoin are OKX, Ethereum must be too)
  const hasOKXSolana = !!(window as any).okxwallet?.solana;
  const hasOKXBitcoin = !!(window as any).okxwallet?.bitcoin;
  const hasOKXMultiChain = hasOKXSolana && hasOKXBitcoin;
  
  // If both Solana and Bitcoin are OKX, then Ethereum is definitely OKX
  if (hasOKXMultiChain && window.ethereum) {
    return "okx";
  }

  // Check for OKX (must be before Bitget, TokenPocket, Binance, and MetaMask!)
  // OKX can be detected via:
  // 1. window.okxwallet.ethereum (primary method)
  // 2. window.ethereum.isOKX (if OKX sets this flag)
  // 3. window.okxwallet exists (even if ethereum provider not immediately available)
  if (window.okxwallet?.ethereum || (window as any).ethereum?.isOKX) {
    return "okx";
  }
  
  // Additional check: if okxwallet exists at all, it's likely OKX
  // (even if ethereum provider isn't available yet, e.g., wallet switched to Bitcoin)
  if ((window as any).okxwallet) {
    return "okx";
  }

  // Check for Bitget (must be before TokenPocket, Binance, and MetaMask!)
  // Bitget uses unique window.bitkeep namespace, so it's easy to detect
  if (isBitgetProviderAvailable()) {
    return "bitget";
  }

  // Check for TokenPocket (must be before Coinbase, Binance, and MetaMask!)
  // TokenPocket uses window.ethereum.isTokenPocket flag (official detection method)
  if (isTokenPocketProviderAvailable()) {
    return "tokenpocket";
  }

  // Check for Rainbow Wallet (must be before Coinbase, Binance, and MetaMask!)
  // Rainbow Wallet injects window.ethereum with isRainbow flag
  const ethereum = window.ethereum as any;
  if (ethereum && ethereum.isRainbow === true) {
    return "rainbow";
  }

  // Check for Coinbase Wallet (must be before Binance and MetaMask!)
  // Uses robust multi-check detection (host, jsonRpcUrl, isWalletLink, etc.)
  if (isCoinbaseWalletProviderAvailableSync()) {
    return "coinbase";
  }

  // Check for Binance (must be before MetaMask!)
  // Uses inpage detection with multiple fallback methods
  if (isBinanceProviderAvailable()) {
    return "binance";
  }

  // Check for MetaMask (must be after OKX, Bitget, TokenPocket, Coinbase, Binance, and Rainbow checks!)
  // Use comprehensive detection to ensure it's truly MetaMask
  if (isMetaMaskProviderAvailable()) {
    return "metamask";
  }

  // If ethereum exists but no specific wallet detected, check if OKX, Bitget, TokenPocket, Coinbase, or Binance exists
  // If OKX exists, it's likely OKX (even if ethereum provider not available)
  if (window.ethereum) {
    // Check Bitget first (unique namespace)
    if (isBitgetProviderAvailable()) {
      return "bitget";
    }
    // Check TokenPocket (uses isTokenPocket flag)
    if (isTokenPocketProviderAvailable()) {
      return "tokenpocket";
    }
    // Check Coinbase Wallet (robust detection)
    if (isCoinbaseWalletProviderAvailableSync()) {
      return "coinbase";
    }
    // Check Rainbow Wallet
    if (window.ethereum && (window.ethereum as any).isRainbow === true) {
      return "rainbow";
    }
    // Only assume MetaMask if comprehensive check passes and other wallets are definitely not present
    const isBinanceDetected = isBinanceProviderAvailable();
    const isCoinbaseDetected = isCoinbaseWalletProviderAvailableSync();
    const isRainbowDetected = window.ethereum && (window.ethereum as any).isRainbow === true;
    if (isMetaMaskProviderAvailable() && !(window as any).okxwallet && !isBinanceDetected && !isCoinbaseDetected && !isRainbowDetected) {
      return "metamask";
    }
    // If okxwallet exists, it's OKX
    if ((window as any).okxwallet) {
      return "okx";
    }
    // If Binance detected, it's Binance
    if (isBinanceDetected) {
      return "binance";
    }
    // If Coinbase detected, it's Coinbase
    if (isCoinbaseDetected) {
      return "coinbase";
    }
  }

  return null;
}

/**
 * Validates if the connected wallet is one of the supported wallets
 */
export function isSupportedWallet(
  wallet: string | null,
): wallet is SupportedWallet {
  return wallet === "metamask" || wallet === "phantom" || wallet === "onekey" || wallet === "okx" || wallet === "binance" || wallet === "bitget" || wallet === "tokenpocket" || wallet === "coinbase" || wallet === "rainbow";
}

/**
 * Custom hook for managing agent delegation operations
 *
 * Handles the complete agent delegation workflow:
 * - Generating owner address from mnemonic (supports multichain)
 * - Generating agent keypair
 * - Preparing agent sign (EIP-712 typed data)
 * - Signing agent approval
 *
 * @param onboardHook - Optional useOnboard hook result to integrate with persisted account data
 * @param initialOwnerMnemonic - Initial owner mnemonic phrase (default: from useOnboard or localStorage, fallback: standard test mnemonic)
 * @param initialExpiresInDays - Initial expiration days (default: 180)
 * @param initialPermissions - Initial permissions string (default: 'clob::submit_order,clob::cancel_order')
 * @param initialAgentName - Initial agent name (default: 'TradingBot')
 * @param initialChainType - Initial chain type for owner address (default: from useOnboard or 'ethereum')
 * @param initialAddressIndex - Initial address index for derivation (default: 0)
 * @param initialIsMainnet - Initial network type (default: true)
 * @returns Object containing:
 *   - ownerMnemonic: Current owner mnemonic phrase
 *   - setOwnerMnemonic: Function to update owner mnemonic
 *   - ownerAddress: Generated owner address
 *   - setOwnerAddress: Function to update owner address
 *   - ownerChainType: Current chain type for owner address
 *   - setOwnerChainType: Function to update chain type
 *   - ownerAddressIndex: Current address index for derivation
 *   - setOwnerAddressIndex: Function to update address index
 *   - ownerIsMainnet: Current network type (mainnet/testnet)
 *   - setOwnerIsMainnet: Function to update network type
 *   - agentAddress: Generated agent address
 *   - setAgentAddress: Function to update agent address
 *   - agentPrivateKey: Generated agent private key
 *   - setAgentPrivateKey: Function to update agent private key
 *   - expiresInDays: Expiration days for delegation
 *   - setExpiresInDays: Function to update expiration days
 *   - permissions: Permissions string (comma-separated)
 *   - setPermissions: Function to update permissions
 *   - agentName: Agent name
 *   - setAgentName: Function to update agent name
 *   - nonce: Nonce for delegation
 *   - setNonce: Function to update nonce
 *   - result: Delegation result or error
 *   - loading: Boolean indicating if operation is in progress
 *   - generateOwnerAddress: Function to generate owner address from mnemonic (uses current chainType)
 *   - generateOwnerAddressForChain: Function to generate owner address for specific chain type
 *   - generateAgentKeypair: Function to generate agent keypair
 *   - prepareAgentSign: Function to prepare agent sign (EIP-712 typed data)
 *   - signAgentApproval: Function to sign agent approval
 *   - isReady: Boolean indicating if mormSigner is ready
 */
export function useAgentDelegation(
  onboardHook?: UseOnboardResult,
  initialOwnerMnemonic?: string,
  initialExpiresInDays: number = 180,
  initialPermissions: string = "clob::submit_order,clob::cancel_order",
  initialAgentName: string = "BrowserUserAgent",
  initialChainType?: ChainType,
  initialAddressIndex: number = 0,
  initialIsMainnet: boolean = true,
) {
  // Get mnemonic from useOnboard hook, localStorage, or fallback to initial value
  const getInitialMnemonic = (): string => {
    if (onboardHook?.mnemonic) return onboardHook.mnemonic;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY_MNEMONIC);
      if (stored) return stored;
    }
    return initialOwnerMnemonic || "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about";
  };

  // Get chain type from useOnboard hook, localStorage, or fallback to initial value
  const getInitialChainType = (): ChainType => {
    if (onboardHook?.chainType) return onboardHook.chainType;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY_CHAIN_TYPE);
      if (stored && ["ethereum", "bitcoin_taproot", "bitcoin_segwit", "bitcoin_legacy", "bitcoin_nested_segwit", "solana"].includes(stored)) {
        return stored as ChainType;
      }
    }
    return initialChainType || "ethereum";
  };

  // Get wallet address from useOnboard hook or localStorage
  const getInitialOwnerAddress = (): string => {
    if (onboardHook?.walletAddress) return onboardHook.walletAddress;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY_WALLET_ADDRESS);
      if (stored) return stored;
    }
    return "";
  };

  const [ownerMnemonic, setOwnerMnemonic] =
    useState<string>(getInitialMnemonic());
  const [ownerAddress, setOwnerAddress] = useState<string>(getInitialOwnerAddress());
  const [ownerChainType, setOwnerChainType] = useState<ChainType>(getInitialChainType());
  const [ownerAddressIndex, setOwnerAddressIndex] = useState<number>(initialAddressIndex);
  const [ownerIsMainnet, setOwnerIsMainnet] = useState<boolean>(initialIsMainnet);
  const [agentAddress, setAgentAddress] = useState<string>("");
  const [agentPrivateKey, setAgentPrivateKey] = useState<string>("");
  const [expiresInDays, setExpiresInDays] =
    useState<number>(initialExpiresInDays);
  const [permissions, setPermissions] = useState<string>(initialPermissions);
  const [agentName, setAgentName] = useState<string>(initialAgentName);
  const [nonce, setNonce] = useState<string>(Date.now().toString());
  const [result, setResult] = useState<AgentDelegationResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Sync with useOnboard hook if provided (single source of truth integration)
  useEffect(() => {
    if (onboardHook) {
      // Update mnemonic from useOnboard if it changes
      if (onboardHook.mnemonic && onboardHook.mnemonic !== ownerMnemonic) {
        setOwnerMnemonic(onboardHook.mnemonic);
      }
      // Update address from useOnboard if it changes and chain type matches
      if (onboardHook.walletAddress && 
          onboardHook.chainType === ownerChainType &&
          onboardHook.walletAddress !== ownerAddress) {
        setOwnerAddress(onboardHook.walletAddress);
      }
      // Update chain type from useOnboard if it changes
      if (onboardHook.chainType && onboardHook.chainType !== ownerChainType) {
        setOwnerChainType(onboardHook.chainType);
      }
    }
  }, [onboardHook?.mnemonic, onboardHook?.walletAddress, onboardHook?.chainType, ownerMnemonic, ownerAddress, ownerChainType]);

  // Restore agent credentials from sessionStorage on mount
  useEffect(() => {
    try {
      const storedAddress = sessionStorage.getItem(STORAGE_KEY_AGENT_ADDRESS);
      const storedPrivateKey = sessionStorage.getItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
      const storedExpiresAt = sessionStorage.getItem(STORAGE_KEY_AGENT_EXPIRES_AT);
      const storedOwnerAddress = sessionStorage.getItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);

      // Only restore if credentials exist and haven't expired
      if (storedAddress && storedPrivateKey && storedExpiresAt) {
        const expiresAt = parseInt(storedExpiresAt, 10);
        const now = Date.now();

        // Check if credentials are still valid (not expired)
        if (!isNaN(expiresAt) && now < expiresAt) {
          // Only restore if owner address matches (credentials are tied to specific owner)
          if (!storedOwnerAddress || !ownerAddress || storedOwnerAddress.toLowerCase() === ownerAddress.toLowerCase()) {
            setAgentAddress(storedAddress);
            setAgentPrivateKey(storedPrivateKey);
          } else {
            // Owner address changed, clear old credentials
            sessionStorage.removeItem(STORAGE_KEY_AGENT_ADDRESS);
            sessionStorage.removeItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
            sessionStorage.removeItem(STORAGE_KEY_AGENT_EXPIRES_AT);
            sessionStorage.removeItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
          }
        } else {
          // Expired, clear storage
          sessionStorage.removeItem(STORAGE_KEY_AGENT_ADDRESS);
          sessionStorage.removeItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
          sessionStorage.removeItem(STORAGE_KEY_AGENT_EXPIRES_AT);
          sessionStorage.removeItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
        }
      }
    } catch (error) {
      // sessionStorage might not be available (e.g., in private browsing)
      console.warn("Failed to restore agent credentials from sessionStorage:", error);
    }
  }, []); // Only run on mount

  // Clear agent credentials when owner address changes (credentials are tied to owner)
  useEffect(() => {
    if (ownerAddress) {
      const storedOwnerAddress = sessionStorage.getItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
      // If owner address changed, clear old agent credentials
      if (storedOwnerAddress && storedOwnerAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
        sessionStorage.removeItem(STORAGE_KEY_AGENT_ADDRESS);
        sessionStorage.removeItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
        sessionStorage.removeItem(STORAGE_KEY_AGENT_EXPIRES_AT);
        sessionStorage.removeItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
        setAgentAddress("");
        setAgentPrivateKey("");
      }
    }
  }, [ownerAddress]);

  // Generate owner address using current chain type (backward compatible)
  const generateOwnerAddress = async (): Promise<void> => {
    await generateOwnerAddressForChain(ownerChainType, ownerAddressIndex, ownerIsMainnet);
  };

  // Generate owner address for specific chain type (multichain support)
  // Uses shared address generation utility to eliminate duplication
  const generateOwnerAddressForChain = async (
    chainType: ChainType = ownerChainType,
    addressIndex: number = ownerAddressIndex,
    isMainnet: boolean = ownerIsMainnet,
  ): Promise<void> => {
    setLoading(true);
    try {
      const addressResult = await generateAddressFromMnemonic(
        ownerMnemonic,
        chainType,
        addressIndex,
        isMainnet,
      );

      if (addressResult.success && addressResult.address) {
        setOwnerAddress(addressResult.address);
        // Update chain type state if different
        if (chainType !== ownerChainType) {
          setOwnerChainType(chainType);
        }
        if (addressIndex !== ownerAddressIndex) {
          setOwnerAddressIndex(addressIndex);
        }
        if (isMainnet !== ownerIsMainnet) {
          setOwnerIsMainnet(isMainnet);
        }
      } else {
        setResult({
          success: false,
          error: addressResult.error || "Failed to generate owner address",
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateAgentKeypair = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    setLoading(true);
    try {
      const keypairResult: KeypairResult = await mormSigner.generateKeypair();
      if (
        keypairResult.success &&
        keypairResult.address &&
        keypairResult.privateKey
      ) {
        setAgentAddress(keypairResult.address);
        setAgentPrivateKey(keypairResult.privateKey);

        // Store agent credentials in sessionStorage (temporary browser storage)
        // These will be automatically cleared when browser closes
        try {
          const expirationTime = Date.now() + (expiresInDays * 24 * 60 * 60 * 1000); // Expires in N days
          sessionStorage.setItem(STORAGE_KEY_AGENT_ADDRESS, keypairResult.address);
          sessionStorage.setItem(STORAGE_KEY_AGENT_PRIVATE_KEY, keypairResult.privateKey);
          sessionStorage.setItem(STORAGE_KEY_AGENT_EXPIRES_AT, expirationTime.toString());
          if (ownerAddress) {
            sessionStorage.setItem(STORAGE_KEY_AGENT_OWNER_ADDRESS, ownerAddress);
          }
        } catch (storageError) {
          // sessionStorage might not be available (e.g., in private browsing)
          console.warn("Failed to store agent credentials in sessionStorage:", storageError);
          // Continue anyway - credentials are still in state
        }
      } else {
        setResult({
          success: false,
          error: keypairResult.error || "Failed to generate agent keypair",
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  const prepareAgentSign = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner and agent addresses are required",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      const result = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      setResult(result);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Wagmi hooks for all wallet support (MetaMask, Phantom, OneKey)
  const { address: connectedAddress, isConnected, connector } = useAccount();
  const { signTypedDataAsync, isPending: isSigning } = useSignTypedData();

  // Helper to get the connected wallet type
  const getConnectedWalletType = (): SupportedWallet | null => {
    if (!isConnected || !connector) return null;

    const detected = detectSupportedWallet();
    if (isSupportedWallet(detected)) {
      return detected;
    }

    // Fallback: check connector name
    const connectorName = connector.name?.toLowerCase() || "";
    if (connectorName.includes("phantom")) return "phantom";
    if (connectorName.includes("onekey")) return "onekey";
    if (connectorName.includes("okx")) return "okx";
    if (connectorName.includes("binance")) return "binance";
    if (connectorName.includes("bitget") || connectorName.includes("bitkeep")) return "bitget";
    if (connectorName.includes("tokenpocket") || connectorName.includes("tp")) return "tokenpocket";
    if (connectorName.includes("coinbase")) return "coinbase";
    if (connectorName.includes("rainbow")) return "rainbow";
    if (connectorName.includes("metamask")) return "metamask";

    return null;
  };

  const signAgentApproval = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your MetaMask wallet first",
      });
      return;
    }

    // Verify it's actually MetaMask
    const walletType = getConnectedWalletType();
    if (walletType !== "metamask") {
      const detected = detectSupportedWallet();
      if (detected === "metamask") {
        setResult({
          success: false,
          error:
            'MetaMask wallet detected but not connected via wagmi. Please use the "Connect MetaMask" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for MetaMask wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in MetaMask.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports MetaMask via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during MetaMask signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const signAgentApprovalWithPhantom = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your Phantom wallet first",
      });
      return;
    }

    // Verify it's actually Phantom
    const walletType = getConnectedWalletType();
    if (walletType !== "phantom") {
      const detected = detectSupportedWallet();
      if (detected === "phantom") {
        setResult({
          success: false,
          error:
            'Phantom wallet detected but not connected via wagmi. Please use the "Connect Phantom" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for Phantom wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in Phantom.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      // wagmi expects { domain, types, primaryType, message }
      const typedData = prepareResult.typedData as any;

      // Extract the EIP-712 structure
      // The typedData might be in different formats, so we handle both
      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        // Already in the correct format
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        // Try to extract from the structure
        // This handles cases where the WASM returns a different format
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports Phantom via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Phantom signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const signAgentApprovalWithOneKey = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your OneKey wallet first",
      });
      return;
    }

    // Verify it's actually OneKey
    const walletType = getConnectedWalletType();
    if (walletType !== "onekey") {
      const detected = detectSupportedWallet();
      if (detected === "onekey") {
        setResult({
          success: false,
          error:
            'OneKey wallet detected but not connected via wagmi. Please use the "Connect OneKey" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for OneKey wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in OneKey.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports OneKey via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during OneKey signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with Bitget wallet
  const signAgentApprovalWithBitget = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your Bitget wallet first",
      });
      return;
    }

    // Verify it's actually Bitget
    const walletType = getConnectedWalletType();
    if (walletType !== "bitget") {
      const detected = detectSupportedWallet();
      if (detected === "bitget") {
        setResult({
          success: false,
          error:
            'Bitget wallet detected but not connected via wagmi. Please use the "Connect Bitget" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for Bitget wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in Bitget.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports Bitget via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Bitget signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with TokenPocket wallet
  const signAgentApprovalWithTokenPocket = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your TokenPocket wallet first",
      });
      return;
    }

    // Verify it's actually TokenPocket
    const walletType = getConnectedWalletType();
    if (walletType !== "tokenpocket") {
      const detected = detectSupportedWallet();
      if (detected === "tokenpocket") {
        setResult({
          success: false,
          error:
            'TokenPocket wallet detected but not connected via wagmi. Please use the "Connect TokenPocket" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for TokenPocket wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in TokenPocket.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports TokenPocket via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during TokenPocket signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with Coinbase Wallet
  const signAgentApprovalWithCoinbaseWallet = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your Coinbase Wallet first",
      });
      return;
    }

    // Verify it's actually Coinbase Wallet
    const walletType = getConnectedWalletType();
    if (walletType !== "coinbase") {
      const detected = detectSupportedWallet();
      if (detected === "coinbase") {
        setResult({
          success: false,
          error:
            'Coinbase Wallet detected but not connected via wagmi. Please use the "Connect Coinbase Wallet" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for Coinbase Wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in Coinbase Wallet.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports Coinbase Wallet via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Coinbase Wallet signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with Phantom Solana wallet
  const signAgentApprovalWithPhantomSolana = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if Phantom Solana provider is available
    if (!isPhantomSolanaAvailable()) {
      setResult({
        success: false,
        error: "Phantom Solana wallet not found. Please install Phantom wallet extension.",
      });
      return;
    }

    const provider = getPhantomSolanaProvider();
    if (!provider) {
      setResult({
        success: false,
        error: "Phantom Solana provider not available",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Ensure connected to Phantom Solana
      const connectedPublicKey = await connectPhantomSolana(provider);

      // Verify the connected address matches the owner address
      if (connectedPublicKey !== ownerAddress) {
        setResult({
          success: false,
          error: `Connected address (${connectedPublicKey}) does not match owner address (${ownerAddress}). Please switch to the correct account in Phantom.`,
        });
        return;
      }

      // Prepare permissions array
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // Build Solana message payload (similar to EIP-712 structure)
      const messagePayload: SolanaMessagePayload = {
        action: "agent_approval",
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
        timestamp: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
      };

      // Sign message with Phantom Solana
      const signedResult = await signSolanaMessage(provider, messagePayload);

      setResult({
        success: true,
        signature: signedResult.signature,
        typedData: messagePayload, // Store payload for reference
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected") ||
        error.message?.includes("User cancelled")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Phantom Solana signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign agent approval with Bitcoin wallet
  const signAgentApprovalWithBitcoin = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if Bitcoin wallet is available
    if (!isBitcoinWalletAvailable()) {
      setResult({
        success: false,
        error: "Bitcoin wallet not found. Please install Unisat, Xverse, or OKX wallet extension.",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Get Bitcoin address from wallet (if not already set)
      let bitcoinAddress = ownerAddress;
      try {
        const walletAddress = await getBitcoinAddress();
        // Verify the wallet address matches the owner address
        if (walletAddress !== ownerAddress) {
          setResult({
            success: false,
            error: `Wallet address (${walletAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in your Bitcoin wallet.`,
          });
          return;
        }
        bitcoinAddress = walletAddress;
      } catch (error) {
        // If address retrieval fails, use the owner address and continue
        console.warn("Could not get address from wallet, using owner address:", error);
      }

      // Prepare permissions array
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // Build Bitcoin message payload (similar to EIP-712 structure)
      // Include nonce and deadline for replay protection
      const messagePayload: BitcoinMessagePayload = {
        action: "agent_approval",
        owner: bitcoinAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
        timestamp: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
        deadline: (Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toString(16), // Hex deadline
      };

      // Determine preferred signing method (BIP-322 if supported, otherwise BIP-137)
      const wallet = getPreferredBitcoinWallet();
      const preferredMethod = wallet?.supportsBIP322 ? 'bip322' : 'bip137';

      // Sign message with Bitcoin wallet
      const signedResult = await signBitcoinMessage(
        messagePayload,
        bitcoinAddress,
        preferredMethod
      );

      setResult({
        success: true,
        signature: signedResult.signature,
        typedData: messagePayload, // Store payload for reference
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected") ||
        error.message?.includes("User cancelled") ||
        error.message?.includes("user reject")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Bitcoin signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Chain-aware signing method that routes to appropriate signing function
  const signAgentApprovalForChain = async (): Promise<void> => {
    switch (ownerChainType) {
      case "ethereum":
        // Use existing Ethereum signing (MetaMask/Phantom Ethereum/OneKey/Bitget/TokenPocket/Coinbase)
        const walletType = getConnectedWalletType();
        if (walletType === "phantom") {
          return signAgentApprovalWithPhantom();
        } else if (walletType === "onekey") {
          return signAgentApprovalWithOneKey();
        } else if (walletType === "bitget") {
          return signAgentApprovalWithBitget();
        } else if (walletType === "tokenpocket") {
          return signAgentApprovalWithTokenPocket();
        } else if (walletType === "coinbase") {
          return signAgentApprovalWithCoinbaseWallet();
        } else {
          return signAgentApproval();
        }
      case "solana":
        return signAgentApprovalWithPhantomSolana();
      case "bitcoin_taproot":
      case "bitcoin_segwit":
      case "bitcoin_legacy":
      case "bitcoin_nested_segwit":
        return signAgentApprovalWithBitcoin();
      default:
        setResult({
          success: false,
          error: `Signing not yet supported for chain type: ${ownerChainType}`,
        });
    }
  };

  // Check if MetaMask is connected via wagmi (consistent with Phantom and OneKey)
  const isMetaMaskConnected =
    isConnected &&
    getConnectedWalletType() === "metamask" &&
    connectedAddress?.toLowerCase() === ownerAddress.toLowerCase();

  // Check if Phantom Solana is connected
  const isPhantomSolanaConnected = (): boolean => {
    if (ownerChainType !== "solana") return false;
    const provider = getPhantomSolanaProvider();
    if (!provider || !provider.isConnected) return false;
    if (!provider.publicKey) return false;
    return provider.publicKey.toString() === ownerAddress;
  };

  // Check if Bitcoin wallet is connected (synchronous check)
  const isBitcoinWalletConnected = (): boolean => {
    const isBitcoinChain = 
      ownerChainType === "bitcoin_taproot" ||
      ownerChainType === "bitcoin_segwit" ||
      ownerChainType === "bitcoin_legacy" ||
      ownerChainType === "bitcoin_nested_segwit";
    
    if (!isBitcoinChain) return false;
    if (!isBitcoinWalletAvailable()) return false;
    if (!ownerAddress) return false;

    // Note: Full verification requires async call to getBitcoinAddress()
    // This is a best-effort synchronous check
    // The actual address verification happens in signAgentApprovalWithBitcoin()
    return true;
  };

  // Clear agent credentials from both state and sessionStorage
  const clearAgentCredentials = (): void => {
    setAgentAddress("");
    setAgentPrivateKey("");
    try {
      sessionStorage.removeItem(STORAGE_KEY_AGENT_ADDRESS);
      sessionStorage.removeItem(STORAGE_KEY_AGENT_PRIVATE_KEY);
      sessionStorage.removeItem(STORAGE_KEY_AGENT_EXPIRES_AT);
      sessionStorage.removeItem(STORAGE_KEY_AGENT_OWNER_ADDRESS);
    } catch (error) {
      console.warn("Failed to clear agent credentials from sessionStorage:", error);
    }
  };

  return {
    ownerMnemonic,
    setOwnerMnemonic,
    ownerAddress,
    setOwnerAddress,
    ownerChainType,
    setOwnerChainType,
    ownerAddressIndex,
    setOwnerAddressIndex,
    ownerIsMainnet,
    setOwnerIsMainnet,
    agentAddress,
    setAgentAddress,
    agentPrivateKey,
    setAgentPrivateKey,
    expiresInDays,
    setExpiresInDays,
    permissions,
    setPermissions,
    agentName,
    setAgentName,
    nonce,
    setNonce,
    result,
    loading: loading || isSigning,
    generateOwnerAddress,
    generateOwnerAddressForChain,
    generateAgentKeypair,
    prepareAgentSign,
    signAgentApproval,
    signAgentApprovalWithPhantom,
    signAgentApprovalWithOneKey,
    signAgentApprovalWithBitget,
    signAgentApprovalWithTokenPocket,
    signAgentApprovalWithCoinbaseWallet,
    signAgentApprovalWithPhantomSolana,
    signAgentApprovalWithBitcoin,
    signAgentApprovalForChain,
    clearAgentCredentials,
    isMetaMaskConnected,
    isPhantomConnected:
      isConnected &&
      getConnectedWalletType() === "phantom" &&
      ownerChainType === "ethereum" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isPhantomSolanaConnected: isPhantomSolanaConnected(),
    isOneKeyConnected:
      isConnected &&
      getConnectedWalletType() === "onekey" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isOKXConnected:
      isConnected &&
      getConnectedWalletType() === "okx" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isBinanceConnected:
      isConnected &&
      getConnectedWalletType() === "binance" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isBitgetConnected:
      isConnected &&
      getConnectedWalletType() === "bitget" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isTokenPocketConnected:
      isConnected &&
      getConnectedWalletType() === "tokenpocket" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isCoinbaseWalletConnected:
      isConnected &&
      getConnectedWalletType() === "coinbase" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isRainbowConnected:
      isConnected &&
      getConnectedWalletType() === "rainbow" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isPhantomSolanaAvailable: isPhantomSolanaAvailable(),
    isBitcoinWalletAvailable: isBitcoinWalletAvailable(),
    isBitcoinWalletConnected: isBitcoinWalletConnected(),
    isReady: mormSigner.isReady(),
  };
}
